import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as moment from 'moment';


/** One captured panel: the image, and the combination of filters it was taken under. */
export interface DashboardCapture {
    /** The panel's own name, e.g. "Audience insights". */
    panel: string;
    /** The filter combination, already written out, e.g. "Channel: JBR · Metric: OTS". Blank when
     *  the panel has no filters. */
    combination: string;
    /** A JPEG data URL. Not PNG: see {@link PdfExportService.exportResultsDashboard}. */
    image: string;
    widthPx: number;
    heightPx: number;
}

export interface DashboardExportMeta {
    campaignName: string;
    period: string;
    channels: string;
}

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {
    async exportCampaignAnalytics(elementId: string, formData: any) {
        const campaign = formData.campaignWithLiveHistory;
        if (!campaign || typeof campaign !== 'object') return;

        const campaignName = campaign.title || 'Campaign';
        const exportDate = moment().format('YYYY-MM-DD');
        const fileName = `${campaignName} – Campaign Analytics – ${exportDate}.pdf`;

        const startDate = formData.selectedStartDate ? moment(formData.selectedStartDate).format('YYYY-MM-DD') : 'N/A';
        const endDate = formData.selectedEndDate ? moment(formData.selectedEndDate).format('YYYY-MM-DD') : 'N/A';

        const selectedChannels = formData.channels && formData.channels.length > 0 
            ? formData.channels.map((c: any) => {
                return c.channelName || c.title || c.name || 'All Channels';
            }).join(', ') 
            : 'All Channels';

        const elementToCapture = document.getElementById(elementId);
        if (!elementToCapture) {
            console.error('Export content container not found.');
            return;
        }

        const canvas = await html2canvas(elementToCapture, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false
        });
            
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        let currentY = 15;
            
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text(campaignName, 10, currentY);
        currentY += 8;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100);
            
        pdf.text(`Period: ${startDate} to ${endDate}`, 10, currentY);
        currentY += 5;

        const channelText = `Channels: ${selectedChannels}`;
        const splitChannels = pdf.splitTextToSize(channelText, pdfWidth - 20);
        pdf.text(splitChannels, 10, currentY);
        currentY += (splitChannels.length * 5) + 5; 

        const imgWidth = pdfWidth - 20;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', 10, currentY, imgWidth, imgHeight);

        let heightLeft = imgHeight - (pdfHeight - currentY);
        let position = currentY;

        while (heightLeft > 0) {
            pdf.addPage();
            position = heightLeft - imgHeight;
            pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
            heightLeft -= pdfHeight;
        }

        pdf.save(fileName);
    }

    /**
     * The results dashboard as a PDF: every panel, once per combination of its filters.
     *
     * The captures arrive already taken, because only the dashboard knows how to put itself into
     * each combination and wait for its charts to settle. This assembles them: one panel to a
     * block, its combination named above it, and a page break whenever the next block will not fit.
     * A panel is never split across pages unless it is taller than a page on its own.
     */
    async exportResultsDashboard(captures: DashboardCapture[], meta: DashboardExportMeta) {
        if (!captures || !captures.length) {
            return;
        }

        // Compressed, and the captures arrive as JPEG rather than PNG. A full run is some forty
        // panels; as scale-2 PNG data URLs that is on the order of a hundred megabytes of base64,
        // and jsPDF builds the file by joining those strings into one — which overflows the
        // engine's maximum string length and fails the export outright with "Invalid string
        // length". JPEG at high quality is an order of magnitude smaller, and screenshots of
        // charts on a white background lose nothing visible to it.
        const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const usableWidth = pageWidth - margin * 2;

        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DOOH Campaign Results', margin, 18);

        pdf.setFontSize(11);
        pdf.text(meta.campaignName, margin, 26);

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100);
        pdf.text(`Period: ${meta.period}`, margin, 33);
        const channelLines = pdf.splitTextToSize(`Channels: ${meta.channels}`, usableWidth);
        pdf.text(channelLines, margin, 38);

        let cursor = 38 + channelLines.length * 4 + 4;
        let lastPanel = '';

        for (const capture of captures) {
            const imgHeight = (capture.heightPx * usableWidth) / capture.widthPx;
            // The panel's own heading, then the combination it was taken under.
            const headingHeight = capture.panel === lastPanel ? 5 : 11;

            if (cursor + headingHeight + imgHeight > pageHeight - margin && cursor > margin + 5) {
                pdf.addPage();
                cursor = margin;
                // A panel continued onto a new page repeats its name, or the reader loses track of
                // which panel the combination belongs to.
                lastPanel = '';
            }

            if (capture.panel !== lastPanel) {
                pdf.setFontSize(12);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(40);
                pdf.text(capture.panel, margin, cursor + 5);
                cursor += 8;
                lastPanel = capture.panel;
            }

            if (capture.combination) {
                pdf.setFontSize(9);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(110);
                pdf.text(capture.combination, margin, cursor + 3);
                cursor += 5;
            }

            pdf.addImage(capture.image, 'JPEG', margin, cursor, usableWidth, imgHeight, '', 'FAST');
            cursor += imgHeight + 6;
        }

        const exportDate = moment().format('YYYY-MM-DD');
        pdf.save(`${meta.campaignName} – Campaign Results – ${exportDate}.pdf`);
    }
}
